import React from "react";

import { Button } from "@/components/ui/button";

import { ReactComponent as LoadingIcon } from "@assets/svg/loading-spinner.svg";

import { stripBundlePrefix } from "@utils/bundle-name";

// configure PDF.js worker on import
import { DocxPreview } from "./DocxPreview";
import { PdfPreview } from "./PdfPreview";
import "./constants";
import { FilePreviewProps } from "./types";

export type { SnipResult, FilePreviewProps } from "./types";

export const FilePreview: React.FC<FilePreviewProps> = ({
  fileUrl,
  fileType,
  filename,
  isLoading = false,
  fileLoadError = false,
  onRetry,
  className = "",
  snipMode = false,
  onSnipComplete,
  snipResult,
  snipIsLoading = false,
  snipError,
  onSnipResultClose,
  onSaveNote,
  onMapToReportBuilder,
  textSelection,
  onTextSelected,
  onTextSelectionClose,
  showZoomControls = true,
  initialPage,
  ocrHighlight,
}) => {
  // Hide the synthetic `_Bundle #N_` splitter prefix from every display use
  // of `filename` (placeholder text, image `alt`). Strip at the boundary so
  // every caller benefits without needing to remember (MDRS-20).
  const displayName = filename ? stripBundlePrefix(filename) : filename;

  if (isLoading) {
    return (
      <div className="flex h-full flex-1 flex-col items-center justify-center gap-2">
        <LoadingIcon className="size-8 animate-spin" />
        <p className="text-sm text-foreground-muted">Loading document preview...</p>
      </div>
    );
  }

  if (fileLoadError) {
    return (
      <div className="flex h-full flex-1 flex-col items-center justify-center gap-2">
        <p className="font-medium text-destructive">Failed to load document</p>
        <p className="text-sm text-foreground-muted">
          There was a problem loading the document preview.
        </p>
        {onRetry && (
          <Button variant="outline" onClick={onRetry} className="mt-2">
            Try Again
          </Button>
        )}
      </div>
    );
  }

  if (!fileUrl) {
    return (
      <div className="relative flex h-full w-full flex-1 items-center justify-center">
        <div className="flex h-[90%] w-[80%] flex-col bg-background-white p-8 shadow-lg">
          <div className="mb-2 text-xs text-foreground-muted">{displayName}</div>
          <div className="flex flex-1 items-center justify-center">
            <p className="text-foreground-muted">
              Document preview would appear here
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (fileType === "pdf") {
    return (
      <PdfPreview
        fileUrl={fileUrl}
        className={className}
        snipMode={snipMode}
        onSnipComplete={onSnipComplete}
        snipResult={snipResult}
        snipIsLoading={snipIsLoading}
        snipError={snipError}
        onSnipResultClose={onSnipResultClose}
        onSaveNote={onSaveNote}
        onMapToReportBuilder={onMapToReportBuilder}
        textSelection={textSelection}
        onTextSelected={onTextSelected}
        onTextSelectionClose={onTextSelectionClose}
        showZoomControls={showZoomControls}
        onLoadError={onRetry}
        initialPage={initialPage}
        ocrHighlight={ocrHighlight}
      />
    );
  }

  if (fileType === "image") {
    return (
      <div className="flex h-full flex-1 items-center justify-center">
        <img
          src={fileUrl}
          alt={displayName || "Document preview"}
          className="max-h-full max-w-full"
        />
      </div>
    );
  }

  if (fileType === "docx") {
    return <DocxPreview fileUrl={fileUrl} className={className} />;
  }

  return (
    <div className="flex h-full flex-1 flex-col items-center justify-center">
      <p className="text-foreground-muted">
        Document preview not available for this file type
      </p>
      <Button
        variant="outline"
        className="mt-4"
        onClick={() => window.open(fileUrl, "_blank")}
      >
        Download File
      </Button>
    </div>
  );
};
